import { useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import { Table } from "@material-ui/core";
import { Menu } from "../../shared/Menu";
import {
  MainTableHeaderTitle,
  MainTableCell,
  MainTableStatusCell,
} from "../../shared/Style/Style";
import { TrainersTableHeader, TrainersListItem } from "../styles";
import { ContainerWithNavBar } from "../../Gamification/shared/ContainerWithNavBar";
import { SuccessSnackbar } from "../../shared/SuccessSnackbar/SuccessSnackbar";
import { ErrorMessageSnackbar } from "../../shared/ErrorMessageSnackbar/ErrorMessageSnackbar";
import { LoadingProgressOverlay } from "../../shared/LoadingProgress/LoadingProgressOverlay";
import { CustomTablePagination } from "../../shared/CustomTablePagination";
import {
  GetDraftTrainersDocument,
  Trainer,
  useDeleteDraftTrainerMutation,
  useGetDraftTrainersQuery,
  usePublishTrainerMutation,
} from "../../../generated/graphql";

const TrainersListHeader = ["Назва", "Опис", "Статус публікації"];

export const Trainers = () => {
  const { push } = useHistory();
  const { appId } = useParams<{ appId: string }>();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [notificationStatus, setNotificationStatus] = useState({
    error: false,
    success: false,
    message: "",
  });

  const [deleteTrainer, { loading: deleteTrainerLoading }] =
    useDeleteDraftTrainerMutation();
  const [publishTrainer, { loading: publishTrainerLoading }] =
    usePublishTrainerMutation();

  const { data } = useGetDraftTrainersQuery({
    variables: {
      data: {
        limit: rowsPerPage,
        skip: page * rowsPerPage,
      },
    },
    onError: () => {
      setNotificationStatus({
        ...notificationStatus,
        error: true,
        message: "Сталась помилка при завантаженні списку тренажерів",
      });
    },
    fetchPolicy: "cache-and-network",
  });

  const trainersList = data?.getDraftTrainers?.data || [];

  const handleDeleteTrainer = async (id: string) => {
    await deleteTrainer({
      variables: { deleteDraftTrainerId: id },
      onCompleted: (data) => {
        if (data.deleteDraftTrainer) {
          setNotificationStatus({
            ...notificationStatus,
            success: true,
            message: "Тренажер успішно видалено",
          });
          return;
        }
        setNotificationStatus({
          ...notificationStatus,
          error: true,
          message: "Сталась помилка при видаленні тренажера",
        });
      },
      onError: () => {
        setNotificationStatus({
          ...notificationStatus,
          error: true,
          message: "Сталась помилка при видаленні тренажера",
        });
      },
      awaitRefetchQueries: true,
      refetchQueries: [GetDraftTrainersDocument],
    });
  };

  const handlePublish = async (id: string, shouldPublish: boolean) => {
    await publishTrainer({
      variables: {
        publishTrainerId: id,
        isPublish: shouldPublish,
      },
      onCompleted: (data) => {
        if (data.publishTrainer) {
          setNotificationStatus({
            ...notificationStatus,
            success: true,
            message: shouldPublish
              ? "Тренажер успішно опубліковано"
              : "Тренажер успішно знято з публікації",
          });
          setTimeout(() => {
            push(`/mobileApps/${appId}/trainers`);
          }, 1000);
          return;
        }
        setNotificationStatus({
          ...notificationStatus,
          error: true,
          message: shouldPublish
            ? "Сталась помилка при публікації тренажера"
            : "Сталась помилка при знятті тренажера з публікації",
        });
      },
      onError: () => {
        setNotificationStatus({
          ...notificationStatus,
          error: true,
          message: shouldPublish
            ? "Сталась помилка при публікації тренажера"
            : "Сталась помилка при знятті тренажера з публікації",
        });
      },
      awaitRefetchQueries: true,
      refetchQueries: [GetDraftTrainersDocument],
    });
  };

  const menuItems = (trainer: Trainer) => [
    {
      name: "Редагувати",
      action: () => push(`/mobileApps/${appId}/trainers/edit/${trainer.id}`),
    },
    {
      name: trainer.isPublished ? "Зняти з публікації" : "Опублікувати",
      isDisabled: !trainer.isPublished && !trainer.canPublish,
      action: () => handlePublish(trainer.id, !trainer.isPublished),
    },
    {
      name: "Видалити",
      isDisabled: trainer.isPublished,
      action: () => handleDeleteTrainer(trainer.id),
      color: "red",
    },
  ];

  const redirectToTrainerCreatePage = () => {
    push(`/mobileApps/${appId}/trainers/create`);
  };

  const handleChangePage = (
    _: React.MouseEvent<HTMLButtonElement, MouseEvent>,
    newPage: number
  ) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setRowsPerPage(+event.target.value);
    setPage(0);
  };

  const totalTrainers = data?.getDraftTrainers?.total ?? 0;

  return (
    <ContainerWithNavBar
      header="Тренажери"
      addButton={{
        handler: redirectToTrainerCreatePage,
        isDisabled: false,
      }}
    >
      {trainersList?.length > 0 && (
        <CustomTablePagination
          rowsPerPageOptions={[10, 20]}
          count={totalTrainers}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      )}
      <Table>
        <TrainersTableHeader>
          {TrainersListHeader.map((item) => (
            <MainTableHeaderTitle key={item}>{item}</MainTableHeaderTitle>
          ))}
        </TrainersTableHeader>
      </Table>
      <div>
        {trainersList?.map((trainer: Trainer) => (
          <TrainersListItem key={trainer.id}>
            <MainTableCell>{trainer.name}</MainTableCell>
            <MainTableCell>{trainer.description}</MainTableCell>
            <MainTableStatusCell status={trainer.isPublished}>
              {trainer.isPublished ? "Опубліковано" : "Не опубліковано"}
            </MainTableStatusCell>
            <Menu
              vertical={36}
              horizontal={12}
              width={170}
              items={menuItems(trainer)}
            />
          </TrainersListItem>
        ))}
      </div>
      {trainersList?.length > 0 && (
        <CustomTablePagination
          rowsPerPageOptions={[10, 20]}
          count={totalTrainers}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      )}
      <SuccessSnackbar
        open={notificationStatus.success}
        handleClose={() =>
          setNotificationStatus({ ...notificationStatus, success: false })
        }
        message={notificationStatus.message}
        snackbarProps={{
          anchorOrigin: { vertical: "bottom", horizontal: "center" },
        }}
      />
      <ErrorMessageSnackbar
        open={notificationStatus.error}
        message={notificationStatus.message}
        closeHandler={() =>
          setNotificationStatus({ ...notificationStatus, error: false })
        }
      />
      {(deleteTrainerLoading || publishTrainerLoading) && (
        <LoadingProgressOverlay />
      )}
    </ContainerWithNavBar>
  );
};
